import ReportProperty from '@/Enums/Report/BeeswaxReportProperties.enum';
import type { IState } from '@/Interfaces/Slices/ReportingDashboard/Search/State.interface';
import { initialState } from '@/constants/reportingDashboard/search';
import { DEFAULT_LOADING_REQUEST_ID } from '@/constants/slice';
import { fulfilledReportCaseHandler } from '@/helpers/beeswaxReports';
import { pendingCaseHandler, rejectedCaseHandler } from '@/store/asyncThunkHandlers';
import { createSlice } from '@reduxjs/toolkit';
import { getCampaignReport, getTotalReport, reportMap } from './searchThunks';

export const reportingDashboardSearch = createSlice({
  name: 'reportingDashboardSearch',
  initialState,
  reducers: {
    resetSearchReportsLoadingState: state => {
      Object.entries(state).forEach(([key, value]) => {
        if (key.endsWith('RequestId') && !value) {
          (state[key as keyof IState] as string) = DEFAULT_LOADING_REQUEST_ID;
        }
      });
      state.errorCardMessage = '';
    },
  },
  extraReducers: builder => {
    reportMap.forEach(([key, thunk]) => {
      builder
        .addCase(thunk.pending, pendingCaseHandler(key, []))
        .addCase(thunk.rejected, rejectedCaseHandler(key, []))
        .addCase(thunk.fulfilled, fulfilledReportCaseHandler(key)); //
    });

    builder
      .addCase(getTotalReport.pending, pendingCaseHandler('totals'))
      .addCase(getTotalReport.rejected, (state, action) => {
        if (state.totalsRequestId === action.meta.requestId) {
          state.errorCardMessage = 'There seems to be a momentary issue loading your data. Please retry.';
          state.totalsRequestId = null;
        }
      })
      .addCase(getTotalReport.fulfilled, (state, action) => {
        if (state.totalsRequestId === action.meta.requestId) {
          const { totals } = action.payload;

          state.totalsRequestId = null;
          state.totals = totals;
          if (!totals[ReportProperty.IMPRESSIONS]) {
            state.errorCardMessage = "We couldn't find any data. Please adjust your filters and try again.";
          }
        }
      })
      .addCase(getCampaignReport.pending, pendingCaseHandler('campaignReport'))
      .addCase(getCampaignReport.rejected, rejectedCaseHandler('campaignReport', []))
      .addCase(getCampaignReport.fulfilled, (state, action) => {
        const { report, filters } = action.payload;
        if (state.campaignReportRequestId === action.meta.requestId) {
          state.campaignReportRequestId = null;
          state.campaignReport = report;

          if (filters.campaigns.length === 0 && report?.length) {
            state.campaigns = report
              .map(campaign => ({
                value: campaign[ReportProperty.CAMPAIGN_NAME],
                label: campaign[ReportProperty.CAMPAIGN_NAME],
              }))
              .sort((a, b) => a.label.localeCompare(b.label));
          }
        }
      });
  },
});

export const { resetSearchReportsLoadingState } = reportingDashboardSearch.actions;

export default reportingDashboardSearch.reducer;
